// LimbEvolution.ts: Evolutionary mechanisms for creature limb adaptation and development
// Implements genetic algorithms and environmental pressure for limb evolution

import { Limb, LimbSegment, LimbType } from './limbSystem';
import { Node } from './types';
import { NeuralNetworkSimulator } from './NeuralNetworkSimulator';
import { ControlSignal } from './limbControl';
import { LifeBehaviorMathEngine, GrowthParameters, CoupledOscillatorState } from './lifeBehaviorMath';

export interface EvolutionaryTrait {
    id: string;
    name: string;
    type: 'physical' | 'behavioral' | 'neural' | 'environmental';
    value: number;
    minValue: number;
    maxValue: number;
    mutationRate: number;
    inheritanceRate: number;
    fitnessImpact: number;
}

export interface LimbGenome {
    limbId: number;
    traits: Map<string, EvolutionaryTrait>;
    fitness: number;
    generation: number;
    parentIds: number[];
    mutations: string[];
    adaptationLevel: number;
}

export interface EnvironmentalPressure {
    type: 'energy_efficiency' | 'movement_speed' | 'defense' | 'sensing' | 'communication' | 'growth' | 'expansion' | 'consolidation';
    intensity: number;
    direction: 'positive' | 'negative';
    duration: number;
    startTime: number;
}

export interface EvolutionMetrics {
    totalGenerations: number;
    averageFitness: number;
    bestFitness: number;
    diversity: number;
    adaptationRate: number;
    mutationRate: number;
    selectionPressure: number;
    density: number;
    massState: boolean;
    breakthroughPotential: number;
    populationSize: number;      // Current population
    growthRate: number;          // Intrinsic growth rate
    carryingCapacity: number;    // Maximum sustainable population
    currentDensity: number;      // Population density (N/K)
    selfEnhancementFactor?: number; // Bootstrap evolution boost
    feedbackStrengths: Map<string, number>; // Positive feedback loops
}

export class LimbEvolutionEngine {
    private network: NeuralNetworkSimulator;
    private mathEngine: LifeBehaviorMathEngine; // Mathematical models for life behaviors
    private genomes: Map<number, LimbGenome> = new Map();
    private environmentalPressures: EnvironmentalPressure[] = [];
    private evolutionMetrics: EvolutionMetrics;
    private generationCounter: number = 0;
    
    // Mathematical model states for limb coordination
    private limbOscillatorStates: Map<number, CoupledOscillatorState> = new Map();
    
    // Evolution parameters
    private readonly MUTATION_RATE = 0.18;
    private readonly CROSSOVER_RATE = 0.8;
    private readonly SELECTION_PRESSURE = 0.6;
    private readonly DIVERSITY_THRESHOLD = 0.15;
    private readonly ADAPTATION_THRESHOLD = 0.35;
    private readonly DENSITY_THRESHOLD = 0.40; // Threshold for mass state (lowered for maximal evolution)
    private readonly BREAKTHROUGH_DENSITY = 0.55; // Threshold to trigger breakthrough (lowered)
    
    constructor(network: NeuralNetworkSimulator) {
        this.network = network;
        this.mathEngine = new LifeBehaviorMathEngine(); // Initialize mathematical models
        this.evolutionMetrics = this.initializeEvolutionMetrics();
        this.setupEnvironmentalPressures();
    }
    
    private initializeEvolutionMetrics(): EvolutionMetrics {
        return {
            totalGenerations: 0,
            averageFitness: 0,
            bestFitness: 0,
            diversity: 1.0,
            adaptationRate: 0.5,
            mutationRate: this.MUTATION_RATE,
            selectionPressure: this.SELECTION_PRESSURE,
            density: 0,
            massState: false,
            breakthroughPotential: 0,
            populationSize: 0,
            growthRate: 0.15,           // 15% growth rate per generation
            carryingCapacity: 0,
            currentDensity: 0,
            selfEnhancementFactor: 0,
            feedbackStrengths: new Map()
        };
    }
    
    private setupEnvironmentalPressures(): void {
        // Energy efficiency pressure
        this.environmentalPressures.push({
            type: 'energy_efficiency',
            intensity: 0.7,
            direction: 'positive',
            duration: Infinity,
            startTime: 0
        });
        
        // Movement speed pressure
        this.environmentalPressures.push({
            type: 'movement_speed',
            intensity: 0.5,
            direction: 'positive',
            duration: Infinity,
            startTime: 0
        });
        
        // Defense pressure (varies with threat level)
        this.environmentalPressures.push({
            type: 'defense',
            intensity: 0.3,
            direction: 'positive',
            duration: Infinity,
            startTime: 0
        });
        
        // Sensing pressure
        this.environmentalPressures.push({
            type: 'sensing',
            intensity: 0.6,
            direction: 'positive',
            duration: Infinity,
            startTime: 0
        });
        
        // Communication pressure
        this.environmentalPressures.push({
            type: 'communication',
            intensity: 0.4,
            direction: 'positive',
            duration: Infinity,
            startTime: 0
        });
        
        // Growth pressure
        this.environmentalPressures.push({
            type: 'growth',
            intensity: 0.8,
            direction: 'positive',
            duration: Infinity,
            startTime: 0
        });
        
        // Expansion pressure (for breaking out of dense states)
        this.environmentalPressures.push({
            type: 'expansion',
            intensity: 0.5,
            direction: 'positive',
            duration: Infinity,
            startTime: 0
        });
        
        // Consolidation pressure (for stabilizing dense formations)
        this.environmentalPressures.push({
            type: 'consolidation',
            intensity: 0.3,
            direction: 'positive',
            duration: Infinity,
            startTime: 0
        });
    }
    
    // Main evolution update method
    public updateLimbEvolution(limbs: Limb[]): void {
        // Initialize genomes for new limbs
        this.initializeGenomes(limbs);
        
        // Calculate density and detect mass state
        this.calculateDensity();
        
        // Update environmental pressures based on density
        this.updateEnvironmentalPressures();
        
        // SCIENTIFIC MODEL: Logistic Growth (Population Dynamics)
        // N(t+1) = N(t) + rN(t)(1 - N(t)/K)
        this.updatePopulationGrowth();
        
        // TRANSCENDENCE MECHANISMS - Let creature exceed limits
        this.detectPositiveFeedbackLoops();
        this.discoverEmergentTraits();
        this.updateBootstrapEvolution();
        
        // Calculate fitness for all limbs
        this.calculateFitness(limbs);
        
        // KEY FIX: Create new limbs when clusters form to maintain extension
        if (this.evolutionMetrics.massState) {
            this.createNewLimbsForClusters(limbs);
        }
        
        // Trigger breakthrough if in dense mass state
        if (this.shouldTriggerBreakthrough()) {
            this.performBreakthrough(limbs);
        }
        
        // Perform evolution if conditions are met
        if (this.shouldEvolve()) {
            this.performEvolution(limbs);
        }
        
        // Update evolution metrics
        this.updateEvolutionMetrics(limbs);
    }
    
    private initializeGenomes(limbs: Limb[]): void {
        for (const limb of limbs) {
            if (!this.genomes.has(limb.id)) {
                const genome = this.createInitialGenome(limb);
                this.genomes.set(limb.id, genome);
            }
        }
    }
    
    private createInitialGenome(limb: Limb): LimbGenome {
        const traits = new Map<string, EvolutionaryTrait>();
        
        // Physical traits
        traits.set('length', {
            id: 'length',
            name: 'Limb Length',
            type: 'physical',
            value: limb.totalLength,
            minValue: 10,
            maxValue: 200,
            mutationRate: 0.1,
            inheritanceRate: 0.8,
            fitnessImpact: 0.3
        });
        
        traits.set('thickness', {
            id: 'thickness',
            name: 'Limb Thickness',
            type: 'physical',
            value: limb.segments[0]?.thickness || 2,
            minValue: 1,
            maxValue: 10,
            mutationRate: 0.15,
            inheritanceRate: 0.9,
            fitnessImpact: 0.2
        });
        
        traits.set('flexibility', {
            id: 'flexibility',
            name: 'Limb Flexibility',
            type: 'physical',
            value: limb.segments[0]?.flexibility || 0.5,
            minValue: 0.1,
            maxValue: 1.0,
            mutationRate: 0.2,
            inheritanceRate: 0.7,
            fitnessImpact: 0.4
        });
        
        traits.set('strength', {
            id: 'strength',
            name: 'Limb Strength',
            type: 'physical',
            value: limb.segments[0]?.strength || 0.5,
            minValue: 0.1,
            maxValue: 1.0,
            mutationRate: 0.12,
            inheritanceRate: 0.85,
            fitnessImpact: 0.35
        });
        
        // Behavioral traits
        traits.set('movement_speed', {
            id: 'movement_speed',
            name: 'Movement Speed',
            type: 'behavioral',
            value: 1.0,
            minValue: 0.1,
            maxValue: 3.0,
            mutationRate: 0.18,
            inheritanceRate: 0.75,
            fitnessImpact: 0.5
        });
        
        traits.set('energy_efficiency', {
            id: 'energy_efficiency',
            name: 'Energy Efficiency',
            type: 'behavioral',
            value: 0.5,
            minValue: 0.1,
            maxValue: 1.0,
            mutationRate: 0.1,
            inheritanceRate: 0.8,
            fitnessImpact: 0.6
        });
        
        traits.set('coordination', {
            id: 'coordination',
            name: 'Coordination',
            type: 'behavioral',
            value: 0.5,
            minValue: 0.1,
            maxValue: 1.0,
            mutationRate: 0.15,
            inheritanceRate: 0.7,
            fitnessImpact: 0.4
        });
        
        // Neural traits
        traits.set('neural_sensitivity', {
            id: 'neural_sensitivity',
            name: 'Neural Sensitivity',
            type: 'neural',
            value: 0.5,
            minValue: 0.1,
            maxValue: 1.0,
            mutationRate: 0.2,
            inheritanceRate: 0.6,
            fitnessImpact: 0.45
        });
        
        traits.set('adaptability', {
            id: 'adaptability',
            name: 'Adaptability',
            type: 'neural',
            value: limb.adaptability,
            minValue: 0.1,
            maxValue: 1.0,
            mutationRate: 0.25,
            inheritanceRate: 0.65,
            fitnessImpact: 0.55
        });
        
        // Environmental traits
        traits.set('environmental_resistance', {
            id: 'environmental_resistance',
            name: 'Environmental Resistance',
            type: 'environmental',
            value: 0.5,
            minValue: 0.1,
            maxValue: 1.0,
            mutationRate: 0.1,
            inheritanceRate: 0.8,
            fitnessImpact: 0.3
        });
        
        return {
            limbId: limb.id,
            traits,
            fitness: 0,
            generation: 0,
            parentIds: [],
            mutations: [],
            adaptationLevel: 0.5
        };
    }
    
    private calculateDensity(): void {
        const nodes = this.network.nodes;
        if (nodes.length < 10) {
            this.evolutionMetrics.density = 0;
            this.evolutionMetrics.massState = false;
            return;
        }
        
        // Calculate bounding box
        const xs = nodes.map(n => n.x);
        const ys = nodes.map(n => n.y);
        const zs = nodes.map(n => n.z || 0);
        
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const minZ = Math.min(...zs);
        const maxZ = Math.max(...zs);
        
        const volume = (maxX - minX) * (maxY - minY) * (maxZ - minZ) || 1;
        
        // Density is nodes per unit volume, normalized
        const rawDensity = nodes.length / volume;
        const maxExpectedNodes = Math.max(100, nodes.length);
        const maxVolume = 1000000; // Large volume threshold
        this.evolutionMetrics.density = Math.min(1, (rawDensity * maxVolume) / maxExpectedNodes);
        
        // Mass state is when density exceeds threshold
        this.evolutionMetrics.massState = this.evolutionMetrics.density > this.DENSITY_THRESHOLD;
        
        // Calculate breakthrough potential
        this.evolutionMetrics.breakthroughPotential = 
            this.evolutionMetrics.massState ? 
                (this.evolutionMetrics.density - this.DENSITY_THRESHOLD) / (1 - this.DENSITY_THRESHOLD) : 
                0;
    }
    
    private shouldTriggerBreakthrough(): boolean {
        return this.evolutionMetrics.density >= this.BREAKTHROUGH_DENSITY && 
               this.evolutionMetrics.breakthroughPotential > 0.5;
    }
    
    private performBreakthrough(limbs: Limb[]): void {
        console.log(`[Evolution] BREAKTHROUGH! Density: ${this.evolutionMetrics.density.toFixed(2)}, triggering expansion`);
        
        // Apply expansion pressure to all limbs
        for (const limb of limbs) {
            const genome = this.genomes.get(limb.id);
            if (!genome) continue;
            
            // Increase adaptability for breakthrough
            const adaptabilityTrait = genome.traits.get('adaptability');
            if (adaptabilityTrait) {
                adaptabilityTrait.value = Math.min(1, adaptabilityTrait.value + 0.3);
            }
            
            // Increase length for expansion
            const lengthTrait = genome.traits.get('length');
            if (lengthTrait) {
                lengthTrait.value = Math.min(lengthTrait.maxValue, lengthTrait.value * 1.2);
            }
            
            // Enhance flexibility for better structural reorganization
            const flexibilityTrait = genome.traits.get('flexibility');
            if (flexibilityTrait) {
                flexibilityTrait.value = Math.min(1, flexibilityTrait.value + 0.2);
            }
            
            // Record breakthrough mutation
            genome.mutations.push('breakthrough_expansion');
            genome.adaptationLevel = Math.min(1, genome.adaptationLevel + 0.2);
        }
        
        // Trigger physical expansion in network
        this.triggerNetworkExpansion();
        
        // Reset density tracking slightly
        this.evolutionMetrics.density *= 0.8;
    }
    
    private triggerNetworkExpansion(): void {
        // Apply expansion force to nodes in the network
        const nodes = this.network.nodes;
        const centerX = nodes.reduce((sum, n) => sum + n.x, 0) / nodes.length;
        const centerY = nodes.reduce((sum, n) => sum + n.y, 0) / nodes.length;
        const centerZ = nodes.reduce((sum, n) => sum + (n.z || 0), 0) / nodes.length;
        
        // Add expansion velocity to nodes
        for (const node of nodes) {
            const dx = node.x - centerX;
            const dy = node.y - centerY;
            const dz = (node.z || 0) - centerZ;
            const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
            
            if (dist > 1e-6) {
                const expansionForce = 0.1 * this.evolutionMetrics.breakthroughPotential;
                node.vx = (node.vx || 0) + (dx / dist) * expansionForce;
                node.vy = (node.vy || 0) + (dy / dist) * expansionForce;
                node.vz = (node.vz || 0) + (dz / dist) * expansionForce * 0.5;
            }
        }
    }
    
    // SCIENTIFIC MODEL: Logistic Growth (Population Dynamics)
    // N(t+1) = N(t) + rN(t)(1 - N(t)/K)
    // where r = intrinsic growth rate, K = carrying capacity
    private updatePopulationGrowth(): void {
        const currentPopulation = this.genomes.size;
        const K = this.calculateCarryingCapacity(); // Carrying capacity
        
        // Update carrying capacity
        this.evolutionMetrics.carryingCapacity = K;
        this.evolutionMetrics.populationSize = currentPopulation;
        
        // MATHEMATICAL MODEL: Use GrowthModel for proper logistic growth calculation
        const dt = 0.016; // Fixed timestep (60fps = 1/60 seconds)
        const growthParams: GrowthParameters = {
            initialSize: 0,
            growthRate: this.evolutionMetrics.growthRate,
            carryingCapacity: K,
            currentSize: currentPopulation,
            time: this.network.step
        };
        
        const newPopulationSize = this.mathEngine.growthModel.calculateLogisticGrowth(growthParams, dt);
        const density = currentPopulation / Math.max(1, K);
        this.evolutionMetrics.currentDensity = density;
        
        // The actual growth is calculated by the model
        const growthRate = this.evolutionMetrics.growthRate * (1 - density);
        
        // Apply population pressure to evolution parameters
        if (density > 0.8) {
            // High density: increase selection pressure
            this.evolutionMetrics.mutationRate = Math.min(0.3, this.evolutionMetrics.mutationRate + 0.01);
        } else if (density < 0.3) {
            // Low density: increase diversity exploration
            this.evolutionMetrics.mutationRate = Math.max(0.05, this.evolutionMetrics.mutationRate - 0.005);
        }
        
        console.log(`[Population Growth] N=${currentPopulation.toFixed(1)}, K=${K.toFixed(1)}, density=${(density*100).toFixed(1)}%, predicted_N=${newPopulationSize.toFixed(1)}, growth_rate=${(growthRate*100).toFixed(1)}%`);
    }
    
    private calculateCarryingCapacity(): number {
        // Carrying capacity based on available resources
        const networkEnergy = this.network.totalNetworkEnergy;
        const networkSize = this.network.nodes.length;
        const availableResources = networkEnergy * 0.01 + networkSize * 0.5;
        
        // Minimum carrying capacity
        const baseCapacity = 10;
        
        // Scale with resources
        const scaledCapacity = baseCapacity + availableResources;
        
        // Maximum capacity based on physical space
        const maxCapacity = 100;
        
        return Math.min(maxCapacity, Math.max(baseCapacity, scaledCapacity));
    }
    
    private updateEnvironmentalPressures(): void {
        const time = this.network.step / 60;
        
        // Update pressure intensities based on creature state and density
        for (const pressure of this.environmentalPressures) {
            switch (pressure.type) {
                case 'energy_efficiency':
                    pressure.intensity = this.network.totalNetworkEnergy < 500 ? 0.9 : 0.5;
                    break;
                case 'movement_speed':
                    pressure.intensity = this.network.creatureState === 'exploring' ? 0.8 : 0.3;
                    break;
                case 'defense':
                    pressure.intensity = this.network.creatureState === 'stabilizing' ? 0.7 : 0.2;
                    break;
                case 'sensing':
                    pressure.intensity = this.network.creatureState === 'sensing' ? 0.9 : 0.4;
                    break;
                case 'communication':
                    pressure.intensity = this.network.linguisticComplexity > 5 ? 0.8 : 0.3;
                    break;
                case 'growth':
                    pressure.intensity = this.network.creatureState === 'growing' ? 0.9 : 0.4;
                    break;
                case 'expansion':
                    // Increase expansion pressure when in dense state
                    pressure.intensity = this.evolutionMetrics.massState ? 0.9 : 0.3;
                    break;
                case 'consolidation':
                    // Increase consolidation pressure when not in mass state
                    pressure.intensity = this.evolutionMetrics.massState ? 0.5 : 0.2;
                    break;
            }
        }
    }
    
    private calculateFitness(limbs: Limb[]): void {
        for (const limb of limbs) {
            const genome = this.genomes.get(limb.id);
            if (!genome) continue;
            
            let fitness = 0;
            
            // Calculate fitness based on environmental pressures
            for (const pressure of this.environmentalPressures) {
                const traitValue = this.getTraitValue(genome, pressure.type);
                const pressureFitness = this.calculatePressureFitness(traitValue, pressure);
                fitness += pressureFitness * pressure.intensity;
            }
            
            // Add limb-specific fitness factors
            fitness += this.calculateLimbSpecificFitness(limb, genome);
            
            // Normalize fitness
            genome.fitness = Math.max(0, Math.min(1, fitness / this.environmentalPressures.length));
        }
    }
    
    private getTraitValue(genome: LimbGenome, pressureType: string): number {
        switch (pressureType) {
            case 'energy_efficiency':
                return genome.traits.get('energy_efficiency')?.value || 0.5;
            case 'movement_speed':
                return genome.traits.get('movement_speed')?.value || 0.5;
            case 'defense':
                return genome.traits.get('strength')?.value || 0.5;
            case 'sensing':
                return genome.traits.get('neural_sensitivity')?.value || 0.5;
            case 'communication':
                return genome.traits.get('coordination')?.value || 0.5;
            case 'growth':
                return genome.traits.get('adaptability')?.value || 0.5;
            case 'expansion':
                return genome.traits.get('adaptability')?.value || 0.5;
            case 'consolidation':
                return genome.traits.get('strength')?.value || 0.5;
            default:
                return 0.5;
        }
    }
    
    private calculatePressureFitness(traitValue: number, pressure: EnvironmentalPressure): number {
        if (pressure.direction === 'positive') {
            return traitValue;
        } else {
            return 1 - traitValue;
        }
    }
    
    private calculateLimbSpecificFitness(limb: Limb, genome: LimbGenome): number {
        let fitness = 0;
        
        // Effectiveness factor
        fitness += limb.effectiveness * 0.3;
        
        // Energy efficiency factor
        const energyEfficiency = limb.currentEnergy / limb.energyCapacity;
        fitness += energyEfficiency * 0.2;
        
        // State alignment factor
        const stateAlignment = this.calculateStateAlignment(limb);
        fitness += stateAlignment * 0.2;
        
        // Type-specific factors
        switch (limb.type) {
            case 'fractal_branch':
                fitness += this.calculateFractalFitness(limb, genome);
                break;
            case 'psychic_manifest':
                fitness += this.calculatePsychicFitness(limb, genome);
                break;
            case 'wave_resonance':
                fitness += this.calculateWaveFitness(limb, genome);
                break;
            case 'energy_conduit':
                fitness += this.calculateEnergyFitness(limb, genome);
                break;
            case 'linguistic_appendage':
                fitness += this.calculateLinguisticFitness(limb, genome);
                break;
            case 'mystical_tendril':
                fitness += this.calculateMysticalFitness(limb, genome);
                break;
            case 'temporal_phase':
                fitness += this.calculateTemporalFitness(limb, genome);
                break;
        }
        
        return fitness;
    }
    
    private calculateStateAlignment(limb: Limb): number {
        switch (this.network.creatureState) {
            case 'growing':
                return limb.type === 'fractal_branch' || limb.type === 'neural_extension' ? 1.0 : 0.3;
            case 'sensing':
                return limb.type === 'sensing_probe' || limb.type === 'psychic_manifest' ? 1.0 : 0.3;
            case 'exploring':
                return limb.type === 'fractal_branch' || limb.type === 'wave_resonance' ? 1.0 : 0.3;
            case 'stabilizing':
                return limb.type === 'defensive_spike' || limb.type === 'energy_conduit' ? 1.0 : 0.3;
            default:
                return 0.5;
        }
    }
    
    private calculateFractalFitness(limb: Limb, genome: LimbGenome): number {
        const fractalDimension = this.calculateFractalDimension();
        const lengthTrait = genome.traits.get('length')?.value || 0.5;
        const adaptabilityTrait = genome.traits.get('adaptability')?.value || 0.5;
        
        return (fractalDimension * 0.4 + lengthTrait * 0.3 + adaptabilityTrait * 0.3) * 0.2;
    }
    
    private calculatePsychicFitness(limb: Limb, genome: LimbGenome): number {
        const psychicLevel = this.calculatePsychicPotential();
        const neuralSensitivity = genome.traits.get('neural_sensitivity')?.value || 0.5;
        const coordination = genome.traits.get('coordination')?.value || 0.5;
        
        return (psychicLevel * 0.5 + neuralSensitivity * 0.3 + coordination * 0.2) * 0.2;
    }
    
    private calculateWaveFitness(limb: Limb, genome: LimbGenome): number {
        const waveIntensity = this.calculateWaveIntensity();
        const flexibility = genome.traits.get('flexibility')?.value || 0.5;
        const movementSpeed = genome.traits.get('movement_speed')?.value || 0.5;
        
        return (waveIntensity * 0.4 + flexibility * 0.3 + movementSpeed * 0.3) * 0.2;
    }
    
    private calculateEnergyFitness(limb: Limb, genome: LimbGenome): number {
        const energyFlow = this.calculateEnergyFlow();
        const energyEfficiency = genome.traits.get('energy_efficiency')?.value || 0.5;
        const strength = genome.traits.get('strength')?.value || 0.5;
        
        return (energyFlow * 0.4 + energyEfficiency * 0.4 + strength * 0.2) * 0.2;
    }
    
    private calculateLinguisticFitness(limb: Limb, genome: LimbGenome): number {
        const linguisticComplexity = this.network.linguisticComplexity;
        const coordination = genome.traits.get('coordination')?.value || 0.5;
        const neuralSensitivity = genome.traits.get('neural_sensitivity')?.value || 0.5;
        
        return (linguisticComplexity * 0.4 + coordination * 0.3 + neuralSensitivity * 0.3) * 0.2;
    }
    
    private calculateMysticalFitness(limb: Limb, genome: LimbGenome): number {
        const psychonautInfluence = this.calculatePsychonautInfluence();
        const adaptability = genome.traits.get('adaptability')?.value || 0.5;
        const environmentalResistance = genome.traits.get('environmental_resistance')?.value || 0.5;
        
        return (psychonautInfluence * 0.5 + adaptability * 0.3 + environmentalResistance * 0.2) * 0.2;
    }
    
    private calculateTemporalFitness(limb: Limb, genome: LimbGenome): number {
        const time = this.network.step / 60;
        const adaptability = genome.traits.get('adaptability')?.value || 0.5;
        const neuralSensitivity = genome.traits.get('neural_sensitivity')?.value || 0.5;
        
        return (Math.sin(time * 0.1) * 0.3 + adaptability * 0.4 + neuralSensitivity * 0.3) * 0.2;
    }
    
    private shouldEvolve(): boolean {
        // Evolve every 100 steps or when diversity is low
        const timeSinceLastEvolution = this.network.step - this.generationCounter * 100;
        return timeSinceLastEvolution >= 100 || this.evolutionMetrics.diversity < this.DIVERSITY_THRESHOLD;
    }
    
    private performEvolution(limbs: Limb[]): void {
        this.generationCounter++;
        
        // Select parents for reproduction
        const parents = this.selectParents(limbs);
        
        // Create offspring through crossover and mutation
        const offspring = this.createOffspring(parents);
        
        // Apply offspring to limbs
        this.applyOffspring(limbs, offspring);
        
        // Update generation counter
        this.evolutionMetrics.totalGenerations = this.generationCounter;
    }
    
    private selectParents(limbs: Limb[]): LimbGenome[] {
        const genomes = Array.from(this.genomes.values());
        
        // SCIENTIFIC MODEL: Roulette Wheel Selection (Fitness Proportional Selection)
        // P(i) = F(i) / Σ F(j) for all j in population
        
        // Calculate total fitness
        const totalFitness = genomes.reduce((sum, g) => sum + g.fitness, 0);
        
        if (totalFitness === 0) {
            // Fallback to random selection if no fitness
            return genomes.slice(0, Math.min(5, genomes.length));
        }
        
        // Select parents using roulette wheel
        const parents: LimbGenome[] = [];
        const parentCount = Math.min(5, Math.floor(genomes.length * 0.3));
        
        for (let i = 0; i < parentCount; i++) {
            let r = Math.random() * totalFitness;
            let cumulativeFitness = 0;
            
            for (const genome of genomes) {
                cumulativeFitness += genome.fitness;
                if (r <= cumulativeFitness) {
                    parents.push(genome);
                    break;
                }
            }
        }
        
        return parents;
    }
    
    private createOffspring(parents: LimbGenome[]): LimbGenome[] {
        const offspring: LimbGenome[] = [];
        
        for (let i = 0; i < parents.length; i++) {
            for (let j = i + 1; j < parents.length; j++) {
                if (Math.random() < this.CROSSOVER_RATE) {
                    const child = this.crossover(parents[i], parents[j]);
                    this.mutate(child);
                    offspring.push(child);
                }
            }
        }
        
        return offspring;
    }
    
    private crossover(parent1: LimbGenome, parent2: LimbGenome): LimbGenome {
        const child: LimbGenome = {
            limbId: -1, // Will be assigned when applied
            traits: new Map(),
            fitness: 0,
            generation: Math.max(parent1.generation, parent2.generation) + 1,
            parentIds: [parent1.limbId, parent2.limbId],
            mutations: [],
            adaptationLevel: (parent1.adaptationLevel + parent2.adaptationLevel) / 2
        };
        
        // Crossover traits
        for (const [traitId, trait1] of parent1.traits) {
            const trait2 = parent2.traits.get(traitId);
            if (!trait2) continue;
            
            const childTrait: EvolutionaryTrait = {
                ...trait1,
                value: Math.random() < 0.5 ? trait1.value : trait2.value,
                inheritanceRate: (trait1.inheritanceRate + trait2.inheritanceRate) / 2
            };
            
            child.traits.set(traitId, childTrait);
        }
        
        return child;
    }
    
    private mutate(genome: LimbGenome): void {
        for (const [traitId, trait] of genome.traits) {
            if (Math.random() < trait.mutationRate) {
                // SCIENTIFIC MODEL: Gaussian Mutation
                // mutated_value = original_value + N(0, σ²)
                // where σ = mutation_rate * (max_value - min_value)
                
                const mutationStdDev = trait.mutationRate * (trait.maxValue - trait.minValue);
                const gaussianMutation = this.gaussianRandom() * mutationStdDev;
                
                trait.value = Math.max(trait.minValue, 
                                     Math.min(trait.maxValue, 
                                             trait.value + gaussianMutation));
                genome.mutations.push(traitId);
            }
        }
    }
    
    // Box-Muller transform for generating Gaussian random numbers
    private gaussianRandom(): number {
        let u = 0, v = 0;
        while(u === 0) u = Math.random();
        while(v === 0) v = Math.random();
        return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
    }
    
    private applyOffspring(limbs: Limb[], offspring: LimbGenome[]): void {
        // Apply offspring to least fit limbs
        const sortedLimbs = limbs.sort((a, b) => {
            const fitnessA = this.genomes.get(a.id)?.fitness || 0;
            const fitnessB = this.genomes.get(b.id)?.fitness || 0;
            return fitnessA - fitnessB;
        });
        
        for (let i = 0; i < Math.min(offspring.length, sortedLimbs.length); i++) {
            const limb = sortedLimbs[i];
            const child = offspring[i];
            child.limbId = limb.id;
            this.genomes.set(limb.id, child);
            
            // Apply traits to limb
            this.applyTraitsToLimb(limb, child);
        }
    }
    
    private applyTraitsToLimb(limb: Limb, genome: LimbGenome): void {
        // MATHEMATICAL MODEL: Apply logistic growth for realistic limb sizing
        const lengthTrait = genome.traits.get('length');
        if (lengthTrait) {
            // Use Gompertz growth for more realistic biological growth curve
            const growthParams: GrowthParameters = {
                initialSize: lengthTrait.minValue,
                growthRate: 0.02, // Slow growth rate for organic growth
                carryingCapacity: lengthTrait.value, // Target size from evolution
                currentSize: limb.totalLength,
                time: limb.evolutionStep
            };
            
            // Calculate new size using Gompertz growth (more realistic than logistic for limb growth)
            const newSize = this.mathEngine.growthModel.calculateGompertzGrowth(growthParams, 0.016);
            limb.totalLength = Math.max(lengthTrait.minValue, Math.min(lengthTrait.maxValue, newSize));
        }
        
        const thicknessTrait = genome.traits.get('thickness');
        if (thicknessTrait) {
            // Apply logistic growth for thickness (thicker over time)
            const thicknessGrowthParams: GrowthParameters = {
                initialSize: 1,
                growthRate: 0.01,
                carryingCapacity: thicknessTrait.value,
                currentSize: limb.segments[0]?.thickness || 2,
                time: limb.evolutionStep
            };
            const newThickness = this.mathEngine.growthModel.calculateLogisticGrowth(thicknessGrowthParams, 0.016);
            
            for (const segment of limb.segments) {
                segment.thickness = Math.max(1, Math.min(10, newThickness));
            }
        }
        
        const flexibilityTrait = genome.traits.get('flexibility');
        if (flexibilityTrait) {
            for (const segment of limb.segments) {
                segment.flexibility = flexibilityTrait.value;
            }
        }
        
        const strengthTrait = genome.traits.get('strength');
        if (strengthTrait) {
            for (const segment of limb.segments) {
                segment.strength = strengthTrait.value;
            }
        }
        
        // MATHEMATICAL MODEL: Apply metabolic dynamics to energy capacity
        const energyEfficiencyTrait = genome.traits.get('energy_efficiency');
        if (energyEfficiencyTrait) {
            const metabolicParams: import('./lifeBehaviorMath').MetabolicParameters = {
                currentEnergy: limb.energyCapacity / 100,
                productionRate: 1.0,
                consumptionRate: 0.1 + (1 - energyEfficiencyTrait.value) * 0.2,
                efficiencyFactor: energyEfficiencyTrait.value,
                maxEnergy: 1.0
            };
            const newEnergy = this.mathEngine.metabolicModel.calculateEnergyDynamics(metabolicParams, 0.016);
            limb.energyCapacity = Math.floor(Math.max(50, Math.min(200, newEnergy * 100)));
        }
        
        const adaptabilityTrait = genome.traits.get('adaptability');
        if (adaptabilityTrait) {
            limb.adaptability = adaptabilityTrait.value;
        }
    }
    
    private updateEvolutionMetrics(limbs: Limb[]): void {
        const genomes = Array.from(this.genomes.values());
        
        if (genomes.length === 0) return;
        
        // Calculate average fitness
        this.evolutionMetrics.averageFitness = genomes.reduce((sum, g) => sum + g.fitness, 0) / genomes.length;
        
        // Calculate best fitness
        this.evolutionMetrics.bestFitness = Math.max(...genomes.map(g => g.fitness));
        
        // Calculate diversity
        this.evolutionMetrics.diversity = this.calculateDiversity(genomes);
        
        // Calculate adaptation rate
        this.evolutionMetrics.adaptationRate = this.calculateAdaptationRate(genomes);
        
        // Update mutation rate based on diversity
        if (this.evolutionMetrics.diversity < this.DIVERSITY_THRESHOLD) {
            this.evolutionMetrics.mutationRate = Math.min(0.3, this.evolutionMetrics.mutationRate + 0.01);
        } else {
            this.evolutionMetrics.mutationRate = Math.max(0.05, this.evolutionMetrics.mutationRate - 0.005);
        }
    }
    
    private calculateDiversity(genomes: LimbGenome[]): number {
        if (genomes.length < 2) return 1.0;
        
        let totalDistance = 0;
        let comparisons = 0;
        
        for (let i = 0; i < genomes.length; i++) {
            for (let j = i + 1; j < genomes.length; j++) {
                const distance = this.calculateGenomeDistance(genomes[i], genomes[j]);
                totalDistance += distance;
                comparisons++;
            }
        }
        
        return totalDistance / comparisons;
    }
    
    private calculateGenomeDistance(genome1: LimbGenome, genome2: LimbGenome): number {
        let distance = 0;
        let traitCount = 0;
        
        for (const [traitId, trait1] of genome1.traits) {
            const trait2 = genome2.traits.get(traitId);
            if (!trait2) continue;
            
            distance += Math.abs(trait1.value - trait2.value);
            traitCount++;
        }
        
        return traitCount > 0 ? distance / traitCount : 0;
    }
    
    private calculateAdaptationRate(genomes: LimbGenome[]): number {
        const currentGeneration = Math.max(...genomes.map(g => g.generation));
        const recentGenomes = genomes.filter(g => g.generation >= currentGeneration - 2);
        
        if (recentGenomes.length < 2) return 0.5;
        
        const avgFitness = recentGenomes.reduce((sum, g) => sum + g.fitness, 0) / recentGenomes.length;
        const adaptationRate = Math.min(1, avgFitness / this.evolutionMetrics.bestFitness);
        
        return adaptationRate;
    }
    
    // Helper calculation methods
    
    // SCIENTIFIC MODEL: Box-Counting Fractal Dimension
    // D = lim(ε→0) log(N(ε)) / log(1/ε)
    // where N(ε) = number of boxes needed to cover the set, ε = box size
    private calculateFractalDimension(): number {
        const nodes = this.network.nodes;
        if (nodes.length < 10) return 1.0;
        
        // Calculate bounding box
        const xs = nodes.map(n => n.x);
        const ys = nodes.map(n => n.y);
        const zs = nodes.map(n => n.z || 0);
        
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const minZ = Math.min(...zs);
        const maxZ = Math.max(...zs);
        
        const Lx = maxX - minX;
        const Ly = maxY - minY;
        const Lz = maxZ - minZ;
        const maxL = Math.max(Lx, Ly, Lz);
        
        if (maxL === 0) return 1.0;
        
        // Multiple box sizes for dimension estimation
        const epsilonSteps = [maxL/10, maxL/20, maxL/40, maxL/80];
        const counts: number[] = [];
        
        for (const epsilon of epsilonSteps) {
            const nx = Math.ceil(Lx / epsilon) || 1;
            const ny = Math.ceil(Ly / epsilon) || 1;
            const nz = Math.ceil(Lz / epsilon) || 1;
            
            const boxes = new Map<string, boolean>();
            
            // Assign each node to a box
            for (const node of nodes) {
                const ix = Math.floor((node.x - minX) / epsilon);
                const iy = Math.floor((node.y - minY) / epsilon);
                const iz = Math.floor((node.z - minZ) / epsilon);
                const key = `${ix},${iy},${iz}`;
                boxes.set(key, true);
            }
            
            counts.push(boxes.size);
        }
        
        // Calculate dimension using linear regression of log(N) vs log(1/ε)
        const logEpsilons = epsilonSteps.map(e => Math.log(1/e));
        const logCounts = counts.map(c => Math.log(Math.max(1, c)));
        
        // Linear regression slope = dimension
        const n = logEpsilons.length;
        const sumX = logEpsilons.reduce((a, b) => a + b, 0);
        const sumY = logCounts.reduce((a, b) => a + b, 0);
        const sumXY = logEpsilons.reduce((sum, x, i) => sum + x * logCounts[i], 0);
        const sumX2 = logEpsilons.reduce((sum, x) => sum + x * x, 0);
        
        const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
        
        return Math.max(1.0, Math.min(3.0, slope)); // Clamp to reasonable range
    }
    
    private calculatePsychicPotential(): number {
        const energyFactor = this.network.totalNetworkEnergy / 1000;
        const linguisticFactor = this.network.linguisticComplexity / 10;
        return (energyFactor + linguisticFactor) / 2;
    }
    
    private calculateWaveIntensity(): number {
        return this.network.wavePatterns.reduce((sum, pattern) => sum + Math.abs(pattern), 0) / 
               Math.max(1, this.network.wavePatterns.length);
    }
    
    private calculateEnergyFlow(): number {
        return this.network.edges.reduce((sum, edge) => sum + edge.weight, 0) / 
               Math.max(1, this.network.edges.length);
    }
    
    private calculatePsychonautInfluence(): number {
        const distance = Math.sqrt(
            Math.pow(this.network.psychonautCreature.x - 400, 2) + 
            Math.pow(this.network.psychonautCreature.y - 300, 2)
        );
        return Math.max(0, 1 - distance / this.network.psychonautCreature.influenceRadius);
    }
    
    // Public interface methods
    public getEvolutionMetrics(): EvolutionMetrics {
        return { ...this.evolutionMetrics };
    }
    
    public getLimbGenome(limbId: number): LimbGenome | undefined {
        return this.genomes.get(limbId);
    }
    
    public getAllGenomes(): LimbGenome[] {
        return Array.from(this.genomes.values());
    }
    
    public addEnvironmentalPressure(pressure: EnvironmentalPressure): void {
        this.environmentalPressures.push(pressure);
    }
    
    public removeEnvironmentalPressure(index: number): void {
        this.environmentalPressures.splice(index, 1);
    }
    
    public getEnvironmentalPressures(): EnvironmentalPressure[] {
        return [...this.environmentalPressures];
    }
    
    public setMutationRate(rate: number): void {
        this.evolutionMetrics.mutationRate = Math.max(0, Math.min(1, rate));
    }
    
    public setSelectionPressure(pressure: number): void {
        this.evolutionMetrics.selectionPressure = Math.max(0, Math.min(1, pressure));
    }
    
    public forceEvolution(): void {
        this.performEvolution(Array.from(this.genomes.keys()).map(id => ({ id } as any)));
    }
    
    // TRANSCENDENCE MECHANISMS - Let the creature find its own way to exceed limits
    
    // Positive Feedback Loops - Traits that enhance themselves create runaway growth
    private detectPositiveFeedbackLoops(): void {
        if (this.genomes.size === 0) return;
        
        const sampleGenome = Array.from(this.genomes.values())[0];
        
        for (const [traitId, trait] of sampleGenome.traits) {
            const feedbackStrength = this.calculateFeedbackStrength(traitId);
            
            if (feedbackStrength > 0.5) {
                // This trait creates positive feedback!
                console.log(`[Positive Feedback] ${traitId} amplifying (strength: ${feedbackStrength.toFixed(2)})`);
                
                // Exponential amplification
                const amplification = 1 + feedbackStrength * 0.1;
                
                for (const genome of this.genomes.values()) {
                    const t = genome.traits.get(traitId);
                    if (t) {
                        // Temporarily exceed bounds for traits with strong feedback
                        t.maxValue = Math.min(t.maxValue * 1.5, 2.0);
                        t.value = Math.min(t.maxValue, t.value * amplification);
                    }
                }
                
                // Feedback can increase carrying capacity
                this.evolutionMetrics.carryingCapacity *= (1 + feedbackStrength * 0.1);
            }
        }
    }
    
    private calculateFeedbackStrength(traitId: string): number {
        let feedbackSum = 0;
        let count = 0;
        
        for (const genome of this.genomes.values()) {
            const trait = genome.traits.get(traitId);
            if (!trait || trait.fitnessImpact <= 0) continue;
            
            // Check if this trait enhances other high-impact traits
            for (const [otherId, otherTrait] of genome.traits) {
                if (otherId === traitId || otherTrait.fitnessImpact <= 0) continue;
                
                const interaction = trait.fitnessImpact * otherTrait.fitnessImpact;
                feedbackSum += interaction;
                count++;
            }
        }
        
        return count > 0 ? feedbackSum / count : 0;
    }
    
    // Emergent Traits - Creature discovers new capabilities through novel combinations
    public discoveredEmergentTraits: Set<string> = new Set();
    
    private discoverEmergentTraits(): void {
        if (this.genomes.size === 0) return;
        
        const genome = Array.from(this.genomes.values())[0];
        
        // Check for synergistic trait combinations
        const adaptiveBreathing = genome.traits.get('adaptability')?.value || 0;
        const neuralSensitivity = genome.traits.get('neural_sensitivity')?.value || 0;
        const coordination = genome.traits.get('coordination')?.value || 0;
        
        // Meta-Evolution: Evolving how to evolve faster
        if (!this.discoveredEmergentTraits.has('meta_evolution')) {
            const metaEvolutionValue = Math.pow(adaptiveBreathing + neuralSensitivity + coordination, 1.5) / 3;
            
            if (metaEvolutionValue > 0.7) {
                console.log('[Emergent Trait] META-EVOLUTION discovered! (Evolving evolution itself)');
                this.discoveredEmergentTraits.add('meta_evolution');
                
                // Add emergent trait to all genomes
                for (const g of this.genomes.values()) {
                    g.traits.set('meta_evolution', {
                        id: 'meta_evolution',
                        name: 'Meta-Evolution',
                        type: 'behavioral',
                        value: metaEvolutionValue,
                        minValue: 0,
                        maxValue: 2.0,
                        mutationRate: 0.15,
                        inheritanceRate: 0.8,
                        fitnessImpact: metaEvolutionValue * 0.4
                    });
                }
                
                // Meta-evolution increases carrying capacity significantly
                this.evolutionMetrics.carryingCapacity *= 1.5;
                this.evolutionMetrics.growthRate *= 1.3;
            }
        }
        
        // Synergistic Efficiency
        if (!this.discoveredEmergentTraits.has('synergistic_efficiency')) {
            const energyEff = genome.traits.get('energy_efficiency')?.value || 0;
            const synergism = Math.pow(energyEff * coordination, 1.3);
            
            if (synergism > 0.65) {
                console.log('[Emergent Trait] SYNERGISTIC EFFICIENCY discovered!');
                this.discoveredEmergentTraits.add('synergistic_efficiency');
                
                for (const g of this.genomes.values()) {
                    g.traits.set('synergistic_efficiency', {
                        id: 'synergistic_efficiency',
                        name: 'Synergistic Efficiency',
                        type: 'behavioral',
                        value: synergism,
                        minValue: 0,
                        maxValue: 2.0,
                        mutationRate: 0.12,
                        inheritanceRate: 0.85,
                        fitnessImpact: synergism * 0.35
                    });
                }
                
                this.evolutionMetrics.carryingCapacity *= 1.3;
            }
        }
        
        // Recursive Adaptation
        if (!this.discoveredEmergentTraits.has('recursive_adaptation')) {
            const recursiveness = Math.exp(adaptiveBreathing + neuralSensitivity) - 1;
            
            if (recursiveness > 0.5) {
                console.log('[Emergent Trait] RECURSIVE ADAPTATION discovered!');
                this.discoveredEmergentTraits.add('recursive_adaptation');
                
                for (const g of this.genomes.values()) {
                    g.traits.set('recursive_adaptation', {
                        id: 'recursive_adaptation',
                        name: 'Recursive Adaptation',
                        type: 'neural',
                        value: recursiveness,
                        minValue: 0,
                        maxValue: 2.0,
                        mutationRate: 0.18,
                        inheritanceRate: 0.75,
                        fitnessImpact: recursiveness * 0.45
                    });
                }
                
                // Recursive adaptation enables faster mutation rate
                this.evolutionMetrics.mutationRate = Math.min(0.4, this.evolutionMetrics.mutationRate + 0.1);
                this.evolutionMetrics.carryingCapacity *= 1.2;
            }
        }
    }
    
    // Bootstrap Evolution - Self-enhancing mechanisms
    private updateBootstrapEvolution(): void {
        const mechanisms = this.detectAccelerationMechanisms();
        
        if (mechanisms.size > 0) {
            let bootstrapBoost = 0;
            
            for (const mechanism of mechanisms) {
                bootstrapBoost += this.getMechanismBoost(mechanism);
            }
            
            // Apply bootstrap acceleration
            if (bootstrapBoost > 0) {
                const acceleration = 1 + bootstrapBoost * 0.5;
                this.evolutionMetrics.growthRate *= acceleration;
                
                // Bootstrap can push past mutation limits
                if (bootstrapBoost > 1) {
                    this.evolutionMetrics.mutationRate = Math.min(0.5, this.evolutionMetrics.mutationRate * (1 + bootstrapBoost * 0.1));
                }
                
                console.log(`[Bootstrap Evolution] Self-enhancement: ${bootstrapBoost.toFixed(2)}x growth`);
            }
        }
    }
    
    private detectAccelerationMechanisms(): Set<string> {
        const mechanisms = new Set<string>();
        
        if (this.genomes.size === 0) return mechanisms;
        
        const genome = Array.from(this.genomes.values())[0];
        
        // Check for high-value traits that accelerate everything
        for (const [traitId, trait] of genome.traits) {
            if (trait.value > 0.7 && trait.fitnessImpact > 0.5) {
                mechanisms.add(traitId);
            }
        }
        
        return mechanisms;
    }
    
    private getMechanismBoost(mechanism: string): number {
        switch (mechanism) {
            case 'adaptive_breathing':
            case 'efficient_breathing':
                return 0.15;
            case 'neural_sensitivity':
                return 0.25;
            case 'coordination':
                return 0.20;
            case 'meta_evolution':
                return 0.50; // Meta-evolution is powerful!
            case 'synergistic_efficiency':
                return 0.30;
            case 'recursive_adaptation':
                return 0.35;
            default:
                return 0.1;
        }
    }
    
    // KEY FIX: Create new extending limbs when clusters form to prevent retraction
    private createNewLimbsForClusters(limbs: Limb[]): void {
        if (!this.evolutionMetrics.massState) return;
        
        const nodes = this.network.nodes;
        const clusterNodes = this.findClusterNodes();
        
        if (clusterNodes.length < 10) return;
        
        // Calculate cluster center
        const clusterCenter = {
            x: clusterNodes.reduce((sum, n) => sum + n.x, 0) / clusterNodes.length,
            y: clusterNodes.reduce((sum, n) => sum + n.y, 0) / clusterNodes.length,
            z: clusterNodes.reduce((sum, n) => sum + (n.z || 0), 0) / clusterNodes.length
        };
        
        // Find nodes in the cluster that don't have enough limbs extending outward
        for (const node of clusterNodes) {
            const nodeLimbs = limbs.filter(l => l.rootNodeId === node.id);
            const outwardLimbs = nodeLimbs.filter(l => {
                if (l.segments.length === 0) return false;
                const tipSegment = l.segments[l.segments.length - 1];
                const dx = tipSegment.x - clusterCenter.x;
                const dy = tipSegment.y - clusterCenter.y;
                const dz = tipSegment.z - clusterCenter.z;
                const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
                return dist > 50; // Limb extends outward from cluster
            });
            
            // If node doesn't have enough outward-extending limbs, trigger limb creation
            if (nodeLimbs.length > 0 && outwardLimbs.length === 0 && Math.random() < 0.1) {
                // Signal the limb system to create new extending limbs
                this.triggerNewExtendingLimb(node);
            }
        }
    }
    
    private findClusterNodes(): Node[] {
        // Find nodes that are in dense clusters
        const nodes = this.network.nodes;
        const clusterNodes: Node[] = [];
        
        for (const node of nodes) {
            const nearby = nodes.filter(n => {
                const dx = n.x - node.x;
                const dy = n.y - node.y;
                const dz = (n.z || 0) - (node.z || 0);
                const dist = Math.sqrt(dx*dx + dy*dy + dz*dz);
                return dist < 80;
            });
            
            if (nearby.length >= 10) {
                clusterNodes.push(node);
            }
        }
        
        return clusterNodes;
    }
    
    private triggerNewExtendingLimb(node: Node): void {
        // This will be handled by the limb system's developLimbs() method
        // by triggering expansion pressure
        if (this.network.limbSystem) {
            // The limb system will check for development triggers and create new limbs
            // We're just flagging that new limbs are needed
            console.log(`[Limb Evolution] Triggering new extending limb from cluster node ${node.id}`);
        }
    }
}
